// =============================================================
// DamAssetModal.jsx — view / edit a single asset (metadata, tags, custom
// fields), download, or delete. Part of the removable DAM module.
// =============================================================
import { useState } from 'react';
import { B, space, font, radius, weight } from '../../constants/brand';
import { api } from '../../api/client';
import { useApi } from '../../hooks/useApi';
import { Modal, Btn, Input, Textarea, Select, Chip, Icon, Spinner, Badge, useToast } from '../../components/ui';
import { formatBytes, categoryOf, iconForCategory, isImageType, downloadAsset, RAW_EDITED_OPTIONS } from './damApi';
import { DamShareModal } from './DamShareModal';
import { DamDownloadModal } from './DamDownloadModal';

const emptyForm = { title: '', description: '', collectionID: '', rawEdited: '', tags: [], customFields: [] };

export function DamAssetModal({ assetId, collections = [], onClose, onChanged }) {
  const { toast } = useToast();
  const { data, loading, reload } = useApi(() => api.get(`dam/assets/${assetId}`), [assetId]);
  const [form, setForm] = useState(null);
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showDownload, setShowDownload] = useState(false);

  // Seed the edit form once the asset loads (or after a reload).
  const seed = (a) => ({
    title: a.title || '',
    description: a.description || '',
    collectionID: a.collectionID || '',
    rawEdited: a.rawEdited || '',
    tags: (a.tags || []).filter(t => t.source === 'manual').map(t => t.name),
    customFields: Array.isArray(a.customFields) ? a.customFields.map(f => ({ ...f })) : [],
  });
  const f = form || (data ? seed(data) : emptyForm);
  const set = (patch) => setForm({ ...f, ...patch });

  const aiTags = (data?.tags || []).filter(t => t.source === 'ai').map(t => t.name);

  const addTag = (raw) => {
    const t = raw.trim().replace(/,$/, '');
    if (!t) return;
    if (!f.tags.some(x => x.toLowerCase() === t.toLowerCase())) set({ tags: [...f.tags, t] });
    setTagInput('');
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`dam/assets/${assetId}`, {
        title: f.title,
        description: f.description,
        collectionId: f.collectionID || null,
        rawEdited: f.rawEdited || '',
        tags: f.tags,
        customFields: f.customFields.filter(cf => (cf.key || '').trim()),
      });
      toast.success('Saved');
      setForm(null);
      await reload();
      onChanged?.();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm('Delete this asset? It will be removed from the library.')) return;
    setDeleting(true);
    try {
      await api.del(`dam/assets/${assetId}`);
      toast.success('Asset deleted');
      onChanged?.();
      onClose();
    } catch (e) { toast.error(e.message); setDeleting(false); }
  };

  const cat = data ? categoryOf(data.fileType) : 'other';
  const showImage = data && isImageType(data.fileType, data.mimeType) && data.previewUrl;

  return (
    <>
    <Modal title={data?.title || data?.fileName || 'Asset'} onClose={onClose} width={860}>
      {loading && !data ? (
        <div style={{ padding: space.xxxl }}><Spinner /></div>
      ) : !data ? (
        <div style={{ padding: space.xl, color: B.textMd }}>Asset not found.</div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.xl, padding: space.xl }}>
          {/* Preview */}
          <div style={{ flex: '1 1 300px', minWidth: 260 }}>
            <div style={{
              background: B.surfaceSunken, borderRadius: radius.xxl, border: `1px solid ${B.border}`,
              minHeight: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
            }}>
              {showImage ? (
                <img src={data.previewUrl} alt={data.title || data.fileName}
                  style={{ maxWidth: '100%', maxHeight: 380, objectFit: 'contain', display: 'block' }} />
              ) : (
                <div style={{ textAlign: 'center', color: B.textMd, padding: space.xxl }}>
                  <Icon name={iconForCategory(cat)} size={48} color={B.textLt} />
                  <div style={{ marginTop: space.sm, fontSize: font.sm }}>{(data.fileType || '').toUpperCase()} file</div>
                </div>
              )}
            </div>
            <div style={{ marginTop: space.md, display: 'flex', gap: space.sm, flexWrap: 'wrap' }}>
              <Btn size="sm" onClick={() => {
                // Images get the size chooser; everything else downloads as-is.
                if (isImageType(data.fileType, data.mimeType)) setShowDownload(true);
                else downloadAsset(assetId).catch(e => toast.error(e.message));
              }}>
                <Icon name="download" size={14} /> Download
              </Btn>
              {data.previewUrl && (
                <Btn size="sm" variant="secondary" onClick={() => window.open(data.previewUrl, '_blank')}>
                  <Icon name="external" size={14} /> Open
                </Btn>
              )}
              <Btn size="sm" variant="ghost" onClick={() => setShowShare(true)}>
                <Icon name="external" size={14} /> Share externally
              </Btn>
            </div>
            <div style={{ marginTop: space.md, fontSize: font.xs, color: B.textMd, lineHeight: 1.7 }}>
              <div><strong>File:</strong> {data.fileName}</div>
              {data.fileSizeBytes != null && <div><strong>Size:</strong> {formatBytes(data.fileSizeBytes)}</div>}
              {data.width && data.height ? <div><strong>Dimensions:</strong> {data.width} × {data.height}px</div> : null}
              {data.uploadedByName && <div><strong>Uploaded by:</strong> {data.uploadedByName}</div>}
              {(data.aiDescription || data.aiMetadata) && (
                <div style={{ marginTop: space.sm, padding: space.sm, background: B.surfaceMuted,
                  borderRadius: radius.lg, color: B.text }}>
                  <Badge color={B.tealDk}><Icon name="sparkles" size={11} /> AI description</Badge>
                  <div style={{ marginTop: 4 }}>
                    {data.aiMetadata?.description || data.aiDescription}
                  </div>
                  {data.aiMetadata && <AiDetail meta={data.aiMetadata} />}
                </div>
              )}
            </div>
          </div>

          {/* Edit form */}
          <div style={{ flex: '1 1 320px', minWidth: 280, display: 'flex', flexDirection: 'column', gap: space.md }}>
            <Input label="Title" value={f.title} onChange={(v) => set({ title: v })} />
            <Textarea label="Description" value={f.description} onChange={(v) => set({ description: v })} rows={3} />
            <Select label="Collection" value={f.collectionID} onChange={(v) => set({ collectionID: v })}
              options={[{ value: '', label: 'No collection' }, ...collections.map(c => ({ value: c.collectionID, label: c.name }))]} />

            {/* Raw vs. Edited — AI-guessed on upload, editable here. */}
            <div>
              <Select label="Raw vs. Edited" value={f.rawEdited} onChange={(v) => set({ rawEdited: v })}
                options={RAW_EDITED_OPTIONS} />
              <div style={{ fontSize: font.xs, color: B.textLt, marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                <Icon name="sparkles" size={11} color={B.tealDk} /> Set automatically on upload — adjust if needed.
              </div>
            </div>

            {/* Manual tags */}
            <div>
              <div style={{ fontSize: font.sm, fontWeight: weight.semibold, color: B.textMd, marginBottom: 6 }}>Tags</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                {f.tags.map(t => (
                  <Chip key={t} color={B.navy} onRemove={() => set({ tags: f.tags.filter(x => x !== t) })}>{t}</Chip>
                ))}
                {f.tags.length === 0 && <span style={{ fontSize: font.xs, color: B.textLt }}>No tags yet</span>}
              </div>
              <Input value={tagInput} onChange={setTagInput} placeholder="Add a tag, press Enter"
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(tagInput); } }} />
            </div>

            {/* AI tags (read-only) */}
            {aiTags.length > 0 && (
              <div>
                <div style={{ fontSize: font.sm, fontWeight: weight.semibold, color: B.textMd, marginBottom: 6,
                  display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Icon name="sparkles" size={13} color={B.tealDk} /> AI tags
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {aiTags.map(t => (
                    <Chip key={t} color={B.tealDk}
                      onClick={!f.tags.some(x => x.toLowerCase() === t.toLowerCase()) ? () => addTag(t) : undefined}>{t}</Chip>
                  ))}
                </div>
                <div style={{ fontSize: font.xs, color: B.textLt, marginTop: 4 }}>Click an AI tag to also keep it as a manual tag.</div>
              </div>
            )}

            {/* Custom fields */}
            <div>
              <div style={{ fontSize: font.sm, fontWeight: weight.semibold, color: B.textMd, marginBottom: 6 }}>
                Custom fields
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {f.customFields.map((cf, i) => (
                  <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input value={cf.key} placeholder="Field (e.g. SKU)"
                      onChange={(e) => set({ customFields: f.customFields.map((x, idx) => idx === i ? { ...x, key: e.target.value } : x) })}
                      style={inputStyle} />
                    <input value={cf.value} placeholder="Value"
                      onChange={(e) => set({ customFields: f.customFields.map((x, idx) => idx === i ? { ...x, value: e.target.value } : x) })}
                      style={inputStyle} />
                    <button onClick={() => set({ customFields: f.customFields.filter((_, idx) => idx !== i) })}
                      style={iconBtnStyle} title="Remove field"><Icon name="trash" size={14} color={B.red} /></button>
                  </div>
                ))}
              </div>
              <Btn size="sm" variant="ghost" onClick={() => set({ customFields: [...f.customFields, { key: '', value: '' }] })}
                style={{ marginTop: 6 }}><Icon name="add" size={13} /> Add field</Btn>
            </div>
          </div>
        </div>
      )}

      {data && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: space.sm,
          padding: `${space.md}px ${space.xl}px`, borderTop: `1px solid ${B.border}` }}>
          <Btn variant="ghost" onClick={remove} loading={deleting} style={{ color: B.red }}>
            <Icon name="trash" size={14} /> Delete
          </Btn>
          <div style={{ display: 'flex', gap: space.sm }}>
            <Btn variant="secondary" onClick={onClose}>Close</Btn>
            <Btn onClick={save} loading={saving}>Save changes</Btn>
          </div>
        </div>
      )}
    </Modal>
    {showShare && data && (
      <DamShareModal
        mode="download"
        asset={{ assetID: assetId, title: data.title, fileName: data.fileName, isImage: isImageType(data.fileType, data.mimeType) }}
        onClose={() => setShowShare(false)} />
    )}
    {showDownload && data && (
      <DamDownloadModal
        asset={{ assetID: assetId, title: data.title, fileName: data.fileName,
          width: data.width, height: data.height, fileSizeBytes: data.fileSizeBytes }}
        onClose={() => setShowDownload(false)} />
    )}
    </>
  );
}

// Compact read-only display of the structured AI metadata (the "more than tags"
// info that powers organic search). Only renders fields Claude actually filled.
function AiDetail({ meta }) {
  if (!meta) return null;
  const rows = [
    ['Composition', meta.composition],
    ['Setting', meta.setting],
    ['Subjects', Array.isArray(meta.subjects) ? meta.subjects.join(', ') : null],
    ['Colors', Array.isArray(meta.colors) ? meta.colors.join(', ') : null],
    ['Activities', Array.isArray(meta.activities) ? meta.activities.join(', ') : null],
    ['Mood', meta.mood],
  ].filter(([, v]) => v && String(v).trim());
  const phrases = Array.isArray(meta.searchPhrases) ? meta.searchPhrases : [];
  if (!rows.length && !phrases.length) return null;
  return (
    <div style={{ marginTop: space.sm, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {rows.map(([label, val]) => (
        <div key={label} style={{ fontSize: font.xs, color: B.textMd }}>
          <strong style={{ color: B.textMd }}>{label}:</strong> {val}
        </div>
      ))}
      {phrases.length > 0 && (
        <div style={{ marginTop: 4 }}>
          <div style={{ fontSize: font.xs, color: B.textLt, marginBottom: 3 }}>Also findable by search:</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {phrases.slice(0, 10).map((p, i) => (
              <span key={i} style={{ fontSize: font.xs, color: B.textMd, background: B.surface,
                border: `1px solid ${B.border}`, borderRadius: radius.md, padding: '1px 7px' }}>{p}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const inputStyle = {
  flex: 1, minWidth: 0, padding: '7px 10px', fontSize: font.base, fontFamily: 'inherit',
  border: `1px solid ${B.border}`, borderRadius: radius.lg, color: B.text, background: B.surface, outline: 'none',
};
const iconBtnStyle = {
  border: 'none', background: 'transparent', cursor: 'pointer', padding: 4, borderRadius: radius.md,
  display: 'flex', alignItems: 'center',
};
